import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { getRoleHomePath, requiresOrganizationOnboarding } from '../../lib/onboarding';
import { FormInput } from '../../components/ui/gaming-login';
import { Lock } from 'lucide-react';
import { consumeInviteRedirect } from '../../lib/inviteRedirect';

/** Supabase may append ?error=... on this URL if OAuth failed (e.g. bad_oauth_state) — not the #access_token email flow. */
function readSupabaseOAuthQueryError(): string | null {
  const q = new URLSearchParams(window.location.search);
  const err = q.get('error');
  if (!err) return null;
  const code = q.get('error_code') || '';
  const desc = (q.get('error_description') || '').replace(/\+/g, ' ');
  const combined = [err, code, desc].filter(Boolean).join(' — ');
  return combined.trim() || err;
}

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const completeEmailConfirmation = useAuthStore((s) => s.completeEmailConfirmation);
  const completeRecovery = useAuthStore((s) => s.completeRecovery);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'loading' | 'signup' | 'recovery' | 'error'>('loading');
  const [accessToken, setAccessToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const oauthErr = readSupabaseOAuthQueryError();
    if (oauthErr) {
      setMode('error');
      setError(
        oauthErr.includes('bad_oauth_state') || oauthErr.includes('OAuth state parameter is invalid')
          ? `Google sign-in did not finish (${oauthErr}). In Supabase: Authentication → URL Configuration → Redirect URLs — include your backend callback pattern, for example https://api.kol.my/api/auth/social/callback** (wildcard), and set BACKEND_PUBLIC_URL on the Railway API to exactly that host with https. Retry from the login page. This is unrelated to linking an existing password account unless Supabase merges identities by email.`
          : oauthErr
      );
      return;
    }

    const hash = window.location.hash;
    if (!hash || hash.length < 2) {
      setMode('error');
      setError('This link is missing confirmation data. Try signing in with your email and password.');
      return;
    }
    const params = new URLSearchParams(hash.slice(1));
    const access_token = params.get('access_token');
    if (!access_token) {
      setMode('error');
      setError('This link does not include a session. Try signing in.');
      return;
    }

    const type = (params.get('type') || '').toLowerCase();
    setAccessToken(access_token);

    if (type === 'recovery') {
      setMode('recovery');
      return;
    }

    setMode('signup');
    let cancelled = false;
    (async () => {
      try {
        await completeEmailConfirmation(access_token);
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        if (cancelled) return;
        const user = useAuthStore.getState().user;
        if (!user) {
          setMode('error');
          setError('Could not load your profile. Try signing in.');
          return;
        }
        if (requiresOrganizationOnboarding(user)) {
          navigate('/onboarding/organization', { replace: true });
        } else {
          const redirect = consumeInviteRedirect();
          if (redirect) {
            navigate(redirect, { replace: true });
            return;
          }
          navigate(getRoleHomePath(user), { replace: true });
        }
      } catch (e: any) {
        if (!cancelled) {
          setMode('error');
          setError(e.response?.data?.error || 'Could not complete sign-in from this link.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [completeEmailConfirmation, navigate]);

  const submitRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPw) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await completeRecovery(accessToken, newPassword);
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      const user = useAuthStore.getState().user;
      if (!user) {
        setError('Could not load your profile. Try signing in.');
        setMode('error');
        return;
      }
      if (requiresOrganizationOnboarding(user)) {
        navigate('/onboarding/organization', { replace: true });
      } else {
        const redirect = consumeInviteRedirect();
        if (redirect) {
          navigate(redirect, { replace: true });
          return;
        }
        navigate(getRoleHomePath(user), { replace: true });
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Could not update password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="theme-agency relative min-h-screen w-full overflow-x-hidden px-4 py-10 text-glass-text login-glass-bg">
      <div className="relative z-10 mx-auto flex w-full max-w-md flex-col items-center gap-8 pb-16">
        <div className="glass-card w-full p-8 shadow-xl text-center">
          {mode === 'error' ? (
            <>
              <h1 className="text-xl font-semibold text-glass-text">Sign-in incomplete</h1>
              <p className="mt-3 text-sm text-glass-text-2">{error}</p>
              <p className="mt-6">
                <Link to="/login" className="font-medium text-accent2 underline-offset-2 hover:underline">
                  Go to sign in
                </Link>
              </p>
            </>
          ) : mode === 'recovery' ? (
            <>
              <h1 className="text-xl font-semibold text-glass-text">Set a new password</h1>
              <p className="mt-2 text-sm text-glass-text-2">Choose a password for your account, then you will be signed in.</p>
              <form onSubmit={submitRecovery} className="mt-6 space-y-4 text-left">
                {error ? (
                  <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-200">
                    {error}
                  </div>
                ) : null}
                <FormInput
                  id="newpw"
                  name="newpw"
                  icon={<Lock className="text-glass-text-3" size={18} />}
                  type="password"
                  placeholder="New password (min 6 characters)"
                  value={newPassword}
                  onChange={(ev) => setNewPassword(ev.target.value)}
                  required
                  disabled={submitting}
                />
                <FormInput
                  id="confpw"
                  name="confpw"
                  icon={<Lock className="text-glass-text-3" size={18} />}
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPw}
                  onChange={(ev) => setConfirmPw(ev.target.value)}
                  required
                  disabled={submitting}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-accent w-full py-3 font-medium disabled:opacity-70"
                >
                  {submitting ? 'Saving…' : 'Save password and sign in'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-glass-text">Finishing sign-in…</h1>
              <p className="mt-3 text-sm text-glass-text-2">Please wait while we connect your account.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
